// ==========================================
// PARTS INVENTORY API - Following API Implementation Guide
// ==========================================
// This file provides React Query hooks and API functions for parts inventory management
// Pattern reference: /docs/api-implementation-guide.md

// 1. IMPORTS
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { API, apiClient } from '../api_clients';
import { useOrganizationStore } from '../stores/organization-store';
import { extractErrorMessage } from './axiosIntance';
import { toast } from 'sonner';
import { useMaintenancePermissions } from '../../hooks/use-rbac';
import {
  mapApiPartToEntity,
  mapApiPartToListItem,
  mapApiPartUsageRecordToEntity,
} from '../mappers/part.mapper';

// 2. TYPE EXPORTS - Re-export entity types
export type { Part, PartListItem, PartSummary, PartUsageRecord } from '../../types/part';

// API request types
export type CreatePartRequest = API.CreatePartRequest;
export type UpdatePartRequest = API.UpdatePartRequest;

// Custom types for query parameters
export interface PartsQueryParams {
  page?: number;
  limit?: number;
  category?: string | null;
  search?: string | null;
  status?: string | null;
}

export interface PaginatedPartsResponse {
  parts: API.ResponsesPart[];
  pagination?: API.ResponsesPaginationInfo;
}

// 3. API FUNCTIONS

/**
 * Fetch paginated list of parts with optional filters
 * Returns raw API response - mapping happens in the hook
 */
async function listParts(
  organizationUnid: string,
  params: PartsQueryParams = {}
): Promise<PaginatedPartsResponse> {
  const response = await apiClient.partsApi.listParts(
    organizationUnid,
    '', // authorization - handled by interceptor
    params.category || undefined,
    params.limit || 25,
    params.page || 1,
    params.search || undefined,
    params.status || undefined
  );

  return {
    parts: response.data.data?.parts || [],
    pagination: response.data.data?.pagination,
  };
}

/**
 * Fetch single part by ID
 * Returns raw API response - mapping happens in the hook
 */
async function getPart(
  organizationUnid: string,
  partId: string
): Promise<API.ResponsesPart> {
  const response = await apiClient.partsApi.getPart(
    organizationUnid,
    partId,
    '' // authorization - handled by interceptor
  );
  const apiPart = response.data.data?.part;
  if (!apiPart) {
    throw new Error('Part not found');
  }
  return apiPart;
}

/**
 * Query params for part usage history
 */
export interface PartUsageQueryParams {
  page?: number;
  limit?: number;
  dateFrom?: string | null;
  dateTo?: string | null;
  workOrderId?: string | null;
  truckId?: string | null;
}

/**
 * Fetch part usage history
 */
async function getPartUsage(
  organizationUnid: string,
  partId: string,
  params: PartUsageQueryParams = {}
): Promise<{ part: API.ResponsesPart; usage: API.ResponsesPartUsageRecord[]; pagination?: API.ResponsesPaginationInfo }> {
  const response = await apiClient.partsApi.getPartUsage(
    organizationUnid,
    partId,
    '', // authorization - handled by interceptor
    params.dateFrom ?? undefined,
    params.dateTo ?? undefined,
    params.limit || 25,
    params.page || 1,
    params.truckId ?? undefined,
    params.workOrderId ?? undefined,
  );
  return {
    part: response.data.data?.part!,
    usage: response.data.data?.usage || [],
    pagination: response.data.data?.pagination,
  };
}

/**
 * Fetch low stock parts
 */
async function getLowStockParts(
  organizationUnid: string
): Promise<{ parts: API.ResponsesPart[]; count: number }> {
  const response = await apiClient.partsApi.getLowStockParts(
    organizationUnid,
    '' // authorization - handled by interceptor
  );
  return {
    parts: response.data.data?.parts || [],
    count: response.data.data?.count || 0,
  };
}

/**
 * Create a new part
 * Returns raw API response - mapping happens in the hook
 */
async function createPart(
  organizationUnid: string,
  data: CreatePartRequest
): Promise<API.ResponsesPart> {
  const response = await apiClient.partsApi.createPart(
    organizationUnid,
    '', // authorization - handled by interceptor
    data
  );
  const apiPart = response.data.data?.part;
  if (!apiPart) {
    throw new Error('Failed to create part');
  }
  return apiPart;
}

/**
 * Update an existing part
 * Returns raw API response - mapping happens in the hook
 */
async function updatePart(
  organizationUnid: string,
  partId: string,
  data: UpdatePartRequest
): Promise<API.ResponsesPart> {
  const response = await apiClient.partsApi.updatePart(
    organizationUnid,
    partId,
    '', // authorization - handled by interceptor
    data
  );
  const apiPart = response.data.data?.part;
  if (!apiPart) {
    throw new Error('Failed to update part');
  }
  return apiPart;
}

/**
 * Archive (soft delete) a part
 */
async function deletePart(
  organizationUnid: string,
  partId: string
): Promise<void> {
  await apiClient.partsApi.deletePart(
    organizationUnid,
    partId,
    '' // authorization - handled by interceptor
  );
}

// 4. REACT QUERY HOOKS

/**
 * Hook to fetch paginated list of parts
 * @param params - Query parameters for filtering and pagination
 * @returns Entity PartListItem[] mapped from API response
 */
export function useParts(params: PartsQueryParams = {}) {
  const { organization } = useOrganizationStore();
  const { canView } = useMaintenancePermissions();

  const query = useQuery({
    queryKey: ['parts', organization?.id, params],
    queryFn: () => listParts(organization?.id || '', params),
    enabled: !!organization?.id && canView,
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (data) => ({
      ...data,
      parts: data.parts.map(apiPart => mapApiPartToListItem(apiPart)),
    }),
  });

  // Handle errors with toast
  useEffect(() => {
    if (query.error) {
      const errorMessage = extractErrorMessage(query.error);
      toast.error('Failed to fetch parts', {
        description: errorMessage,
      });
    }
  }, [query.error]);

  return query;
}

/**
 * Hook to fetch single part by ID
 * @param partId - Part ID to fetch
 * @param enabled - Whether to enable the query
 * @returns Entity Part mapped from API response
 */
export function usePartById(partId: string, enabled: boolean = true) {
  const { organization } = useOrganizationStore();
  const { canView } = useMaintenancePermissions();

  const query = useQuery({
    queryKey: ['parts', organization?.id, partId],
    queryFn: () => getPart(organization?.id || '', partId),
    enabled: enabled && !!organization?.id && !!partId && canView,
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (apiPart) => mapApiPartToEntity(apiPart),
  });

  useEffect(() => {
    if (query.error) {
      const errorMessage = extractErrorMessage(query.error);
      toast.error('Failed to fetch part', {
        description: errorMessage,
      });
    }
  }, [query.error]);

  return query;
}

/**
 * Hook to fetch part usage history with pagination and filters
 * @param partId - Part ID to fetch usage for
 * @param params - Query params (page, limit, dateFrom, dateTo, workOrderId, truckId)
 * @param enabled - Whether to enable the query
 * @returns Part entity + usage records + pagination
 */
export function usePartUsage(partId: string, params: PartUsageQueryParams = {}, enabled: boolean = true) {
  const { organization } = useOrganizationStore();
  const { canView } = useMaintenancePermissions();

  const query = useQuery({
    queryKey: ['parts', organization?.id, partId, 'usage', params],
    queryFn: () => getPartUsage(organization?.id || '', partId, params),
    enabled: enabled && !!organization?.id && !!partId && canView,
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (data) => ({
      part: mapApiPartToEntity(data.part),
      usage: data.usage.map(u => mapApiPartUsageRecordToEntity(u)),
      pagination: data.pagination,
    }),
  });

  useEffect(() => {
    if (query.error) {
      const errorMessage = extractErrorMessage(query.error);
      toast.error('Failed to fetch part usage history', {
        description: errorMessage,
      });
    }
  }, [query.error]);

  return query;
}

/**
 * Hook to fetch low stock parts
 * @returns Array of low stock parts with count
 */
export function useLowStockParts() {
  const { organization } = useOrganizationStore();
  const { canView } = useMaintenancePermissions();

  const query = useQuery({
    queryKey: ['parts', organization?.id, 'low-stock'],
    queryFn: () => getLowStockParts(organization?.id || ''),
    enabled: !!organization?.id && canView,
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (data) => ({
      parts: data.parts.map(apiPart => mapApiPartToEntity(apiPart)),
      count: data.count,
    }),
  });

  useEffect(() => {
    if (query.error) {
      const errorMessage = extractErrorMessage(query.error);
      toast.error('Failed to fetch low stock parts', {
        description: errorMessage,
      });
    }
  }, [query.error]);

  return query;
}

/**
 * Hook to create a new part
 * @returns Mutation for creating a part
 */
export function useCreatePart() {
  const { organization } = useOrganizationStore();
  const queryClient = useQueryClient();
  const { canCreate } = useMaintenancePermissions();

  return useMutation({
    mutationFn: (data: CreatePartRequest) => {
      if (!canCreate) throw new Error('Permission denied: cannot create parts');
      return createPart(organization?.id || '', data);
    },
    onSuccess: (apiPart) => {
      const part = mapApiPartToEntity(apiPart);

      // Invalidate parts list and low stock
      queryClient.invalidateQueries({
        queryKey: ['parts', organization?.id],
      });

      toast.success('Part created successfully!', {
        description: `${part.partNumber} — ${part.description}`,
      });
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Failed to create part', {
        description: errorMessage,
      });
    },
  });
}

/**
 * Hook to update an existing part
 * @returns Mutation for updating a part
 */
export function useUpdatePart() {
  const { organization } = useOrganizationStore();
  const queryClient = useQueryClient();
  const { canUpdate } = useMaintenancePermissions();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePartRequest }) => {
      if (!canUpdate) throw new Error('Permission denied: cannot update parts');
      return updatePart(organization?.id || '', id, data);
    },
    onSuccess: (_apiPart, variables) => {
      // Invalidate specific part
      queryClient.invalidateQueries({
        queryKey: ['parts', organization?.id, variables.id],
      });

      // Invalidate parts list and low stock
      queryClient.invalidateQueries({
        queryKey: ['parts', organization?.id],
      });

      toast.success('Part updated successfully!');
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Failed to update part', {
        description: errorMessage,
      });
    },
  });
}

/**
 * Hook to archive (soft delete) a part
 * @returns Mutation for archiving a part
 */
export function useDeletePart() {
  const { organization } = useOrganizationStore();
  const queryClient = useQueryClient();
  const { canDelete } = useMaintenancePermissions();

  return useMutation({
    mutationFn: (partId: string) => {
      if (!canDelete) throw new Error('Permission denied: cannot delete parts');
      return deletePart(organization?.id || '', partId);
    },
    onSuccess: () => {
      // Invalidate parts list and low stock
      queryClient.invalidateQueries({
        queryKey: ['parts', organization?.id],
      });

      toast.success('Part archived successfully!');
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Failed to archive part', {
        description: errorMessage,
      });
    },
  });
}

// SPECIALIZED HOOKS FOR SPECIFIC USE CASES

/**
 * Hook to search parts (for dropdowns/comboboxes in work orders)
 * @param search - Search query string
 * @param enabled - Whether to enable the query
 */
export function usePartsSearch(search: string, enabled: boolean = true) {
  return useParts({
    search: search || null,
    status: 'active',
    limit: 20,
  });
}

/**
 * Hook to fetch parts by category
 * @param category - Part category to filter by
 */
export function usePartsByCategory(category: string) {
  return useParts({
    category,
    limit: 100,
  });
}
